import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, Euro, Shield, TrendingUp, ArrowRight, CheckCircle, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

// Add font import
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);



// Hero Component - Hero.js
// Główna sekcja hero z animowanym nagłówkiem i kartami usług
const Hero = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mouseX, setMouseX] = useState(0);
  const requestRef = useRef(null);
  const previousTimeRef = useRef(null);
  const currentXRef = useRef(0);
  const targetXRef = useRef(0);

  // Funkcja przewijania do formularza kontaktowego
  const handleContactScroll = () => {
    const contactSection = document.querySelector('#kontakt');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      targetXRef.current = e.clientX;
    };

    const animate = (time) => {
      if (previousTimeRef.current !== undefined) {
        const diff = targetXRef.current - currentXRef.current;
        currentXRef.current += diff * 0.1;
        setMouseX(currentXRef.current);
      }
      previousTimeRef.current = time;
      requestRef.current = requestAnimationFrame(animate);
    };

    window.addEventListener('mousemove', handleMouseMove);
    requestRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, []);

  const getTextOffset = (lineIndex) => {
    if (window.innerWidth < 768) return 0;
    const windowWidth = window.innerWidth;
    const normalizedX = (mouseX - windowWidth / 2) / (windowWidth / 2);
    // Każdy rząd porusza się w przeciwnym kierunku
    return lineIndex % 2 === 0 ? normalizedX * 50 : -normalizedX * 50;
  };
  
  const services = [
    {
      id: 1,
      category: "DOFINANSOWANIA",
      title: "Dotacje unijne i krajowe - maksymalizujemy Twoje szanse",
      description: "Specjalizujemy się w pozyskiwaniu środków z funduszy europejskich i krajowych. Przeprowadzimy Twoją firmę przez cały proces - od analizy możliwości po rozliczenie dotacji.",
      image: "/dotacje.png",
      link: "/pozyskiwanie-dofinansowan"
    },
    {
      id: 2,
      category: "PRAWO GOSPODARCZE",
      title: "Kompleksowa obsługa prawna dla biznesu",
      description: "Zapewniamy pełne wsparcie prawne - od sporządzania umów handlowych, przez reprezentację w sporach gospodarczych, po bieżące doradztwo korporacyjne.",
      image: "/image-temida.png",
      link: "/obsluga-prawna-biznesu" 
    },
    {
      id: 3,
      category: "OPTYMALIZACJE",
      title: "Strategiczne planowanie podatkowe i finansowe",
      description: "Tworzymy optymalne struktury podatkowe i pomagamy w pozyskaniu finansowania zewnętrznego. Minimalizujemy obciążenia przy zachowaniu pełnego bezpieczeństwa prawnego.",
      image: "/image-optymalizacja.png",
      link: "/podatki-finansowanie"
    },
    {
      id: 4,
      category: "CONSULTING",
      title: "Doradztwo strategiczne i rozwój biznesu",
      description: "Wspieramy przedsiębiorców w kluczowych decyzjach biznesowych. Od analizy rynku, przez strategie rozwoju, po procesy fuzji i przejęć.",
      image: "/consulting.png",
      link: "/doradztwo-strategiczne"
    }
  ];

  return (
    <section id="hero" className="pt-28 pb-16 md:py-24 bg-black overflow-hidden">
      <div className="mb-10 md:mb-16 pt-6 md:pt-12 w-full px-4">
        <h2 className="text-[1.72rem] sm:text-3xl md:text-5xl lg:text-6xl font-black text-white mb-4 leading-[1.08] md:leading-[1.25] tracking-normal md:tracking-wider text-center mx-auto w-full max-w-6xl" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
          <div className="md:px-8">
            <div
              className="transition-transform duration-700 ease-out"
              style={{ transform: `translateX(${getTextOffset(0)}px)` }}
            >
              ZOBACZ, JAKI POTENCJAŁ MA
            </div>
          </div>
          <div className="md:px-8">
            <div
              className="transition-transform duration-700 ease-out"
              style={{ transform: `translateX(${getTextOffset(1)}px)` }}
            >
              TWOJA FIRMA, GDY ZYSKA
            </div>
          </div>
          <div>
            <div
              className="transition-transform duration-700 ease-out"
              style={{ transform: `translateX(${getTextOffset(2)}px)` }}
            >
              FINANSOWANIE I WSPARCIE STRATEGICZNE
            </div>
          </div>
        </h2>
        <div className="flex justify-center mt-8 md:mt-12 px-4">
          <button 
            onClick={handleContactScroll}
            className="group flex items-center justify-center gap-2 md:gap-3 text-white text-lg sm:text-xl lg:text-3xl font-medium hover:text-violet-500 transition-all duration-300 uppercase text-center"
          >
            <span>Czas na realny wzrost</span>
            <ChevronRight className="w-6 h-6 md:w-8 md:h-8 flex-shrink-0 text-violet-600 group-hover:text-violet-500 group-hover:translate-x-2 group-hover:scale-110 transition-all duration-300" strokeWidth={5} />
          </button>
        </div>
      </div>
      
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {services.map((service) => (
            <Link
              key={service.id}
              to={service.link}
              className="group relative bg-gray-900 rounded-lg overflow-hidden cursor-pointer transform transition-all duration-500 md:hover:scale-105 block"
              onMouseEnter={() => setHoveredCard(service.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="relative h-[320px] sm:h-[360px] lg:h-[400px] overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 brightness-110 contrast-110 saturate-125"                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent"></div>                
                <div className="absolute top-4 left-4 right-4">
                  <span className="text-xs font-bold text-white tracking-wider block mb-2" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.9), 0 0 20px rgba(0,0,0,0.7)' }}>
                    {service.category}
                  </span>
                  <h3 className="text-lg font-bold text-white leading-tight" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.9), 0 0 20px rgba(0,0,0,0.7)' }}>
                    {service.title}
                  </h3>
                </div>
                
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className={`transition-all duration-500 ${hoveredCard === service.id ? 'max-h-40 opacity-100' : 'max-h-28 opacity-100 md:max-h-0 md:opacity-0'} overflow-hidden`}>
                    <p className="text-sm text-gray-300 mb-4">
                      {service.description}
                    </p>
                    <span className="inline-flex items-center gap-2 text-white hover:text-violet-500 transition-colors">
                      <span className="text-sm font-medium">Dowiedz się więcej</span>
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Main App Component
export default function App() {
  return (
    <div className="min-h-screen bg-black">
      <Hero />
    </div>
  );
}